/**
 * Data-source presentation for chart headers.
 * Maps raw feed codes (API `source`, socket `via`, health) → professional labels.
 * Never exposes transient connection states (reconnecting / polling) to the UI.
 */

import { isMarketOpen } from './marketUtils';

/** @typedef {'live'|'nse_realtime'|'delayed'|'historical'|'offline'|'idle'} DataSourceKind */

/**
 * Raw strings seen from api_ohlc_live.php / Socket.IO payloads.
 * @param {string} raw
 * @returns {DataSourceKind|null}
 */
function kindFromRaw(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (!s) return null;

  if (s === 'ltp' || s.includes('upstox') || s === 'ws' || s === 'websocket') {
    return 'nse_realtime';
  }
  if (s === 'live_feeder' || s === 'live' || s.includes('forming')) {
    return 'live';
  }
  if (s === 'db-poll' || s === 'db' || s === 'redis' || s === 'mysql') {
    return 'live'; // continuous feed during session; refined by market hours below
  }
  if (s === 'delayed' || s.includes('delay') || s.includes('stale')) {
    return 'delayed';
  }
  if (s === 'historical' || s === 'eod' || s === 'cache') {
    return 'historical';
  }
  if (s === 'offline' || s === 'error') {
    return 'offline';
  }
  return null;
}

/**
 * Resolve a stable data-source kind for the badge.
 *
 * @param {object} opts
 * @param {string} [opts.source]  API payload.source
 * @param {string} [opts.via]     Socket via (ltp | db-poll | redis | …)
 * @param {string} [opts.mode]    Internal feed mode (live|polling|reconnecting|delayed|offline|idle)
 * @param {boolean} [opts.socketLive]
 * @param {boolean} [opts.intraday]
 * @param {number|null} [opts.lastSuccess] epoch ms of last good data
 * @param {number} [opts.now]     for tests
 * @returns {{ kind: DataSourceKind, label: string, tone: string, title: string }}
 */
export function resolveDataSource({
  source = '',
  via = '',
  mode = '',
  socketLive = false,
  intraday = true,
  lastSuccess = null,
  now = Date.now(),
} = {}) {
  const marketOpen = isMarketOpen(new Date(now));
  const age = lastSuccess ? now - lastSuccess : null;

  // Explicit delayed / offline from health monitor (not connection chrome)
  if (mode === 'offline' && marketOpen && (age == null || age > 30000)) {
    return meta('offline');
  }
  if (mode === 'delayed' && marketOpen) {
    return meta('delayed');
  }
  if (marketOpen && age != null && age > 8000) {
    return meta(age > 45000 ? 'offline' : 'delayed');
  }

  // Prefer the most specific raw code
  let kind = kindFromRaw(via) || kindFromRaw(source);

  // Socket live during market → elevate to NSE Realtime when LTP path is active
  if (socketLive && marketOpen) {
    if (via === 'ltp' || via === 'upstox-ltp' || String(source).toLowerCase().includes('upstox')) {
      kind = 'nse_realtime';
    } else if (!kind || kind === 'historical') {
      kind = 'live';
    }
  }

  // Silent recovery paths (reconnecting / polling) — keep last known kind; never surface them
  if (!kind && (mode === 'reconnecting' || mode === 'polling' || mode === 'connecting')) {
    kind = marketOpen && intraday ? 'live' : (intraday ? 'historical' : 'historical');
  }

  if (!kind) {
    if (!intraday) kind = 'historical';
    else if (!marketOpen) kind = 'historical';
    else if (mode === 'live' || socketLive) kind = 'live';
    else if (mode === 'offline') kind = 'offline';
    else if (mode === 'idle') kind = 'idle';
    else kind = marketOpen ? 'live' : 'historical';
  }

  // After hours: continuous feeds are historical
  if ((kind === 'live' || kind === 'nse_realtime') && !marketOpen && intraday) {
    kind = 'historical';
  }
  if (!intraday) {
    kind = 'historical';
  }

  return meta(kind, { source, via, lastSuccess, age });
}

/**
 * @param {DataSourceKind} kind
 * @param {object} [extra]
 */
function meta(kind, extra = {}) {
  const map = {
    live: {
      kind: 'live',
      label: 'Live',
      tone: 'live',
      title: 'Live market data feed',
    },
    nse_realtime: {
      kind: 'nse_realtime',
      label: 'NSE Realtime',
      tone: 'realtime',
      title: 'NSE index realtime (Upstox LTP forming bars)',
    },
    delayed: {
      kind: 'delayed',
      label: 'Delayed',
      tone: 'delayed',
      title: 'Feed delayed — last bar may lag the market',
    },
    historical: {
      kind: 'historical',
      label: 'Historical',
      tone: 'historical',
      title: 'Historical / session-closed data',
    },
    offline: {
      kind: 'offline',
      label: 'Offline',
      tone: 'offline',
      title: 'No recent market data',
    },
    idle: {
      kind: 'idle',
      label: '',
      tone: 'idle',
      title: '',
    },
  };
  const base = map[kind] || map.idle;
  const bits = [];
  if (extra.via) bits.push(`via ${extra.via}`);
  if (extra.source && extra.source !== extra.via) bits.push(`source ${extra.source}`);
  if (extra.age != null && extra.age >= 0) {
    bits.push(`last update ${Math.round(extra.age / 1000)}s ago`);
  }
  return {
    ...base,
    title: bits.length ? `${base.title} (${bits.join(' · ')})` : base.title,
  };
}

/** Human label only (for compact meta strings). */
export function dataSourceLabel(opts) {
  return resolveDataSource(opts).label;
}
